import BaseModel from "./baseModel";
import Vue from "vue";
import {RelationType} from "./relationType";
import store from "../state/store";
import IRepo from "../interfaces/repo";
import ModelCollection from "./modelCollection";
import IProvider from "../interfaces/provider";
import {IServerData} from "../interfaces/serverInterfaces";
import {Hash} from "../interfaces/hashInterfaces";
import {IPeekerOptions} from "../services/peeker";
import {StringHelper} from "../helpers/stringHelper";
import {DocumentType, DocumentTypeToModelTypeMap} from "../enums/documentType";
import {commonActions, RouteMap} from "../helpers/modelRouteHelper";
import {ModelType} from "~/enums/modelType";
import {Logger} from "~/services/logger";
import IModel from "~/interfaces/modelInterfaces";

export default class Model extends BaseModel implements IModel {
  id: string;
  client_id: string;
  c_at: string;
  u_at: string;
  parent: any = null;
  except: string[] = [];
  _rootModel: any;

  get rootModel(): IModel {
    if (this._rootModel) {
      return this._rootModel;
    } else if (this.parent) {
      return this.parent.rootModel;
    } else {
      return null;
    }
  }

  set rootModel(newVal) {
    this._rootModel = newVal;
  }

  protected _lastPeekerOptions: IPeekerOptions;

  get lastPeekerOptions(): IPeekerOptions {
    return this._lastPeekerOptions;
  }

  set lastPeekerOptions(newVal) {
    this._lastPeekerOptions =  newVal;
  }

  static repo: IRepo<Model, IProvider<Model>>;
  static get provider(): IProvider<Model> {
    return this.repo.provider;
  }

  get repo(): IRepo<this, IProvider<this>> {
    return <IRepo<this, IProvider<this>>>(<any>this.constructor).repo;
  }

  get provider(): this['repo']['provider'] {
    return this.repo.provider;
  }

  select(): void {
    if (import.meta.env.MODE === 'development') {
      throw new Error("Method not implemented yet");
    }
  }

  private _collectionReferences: Set<ModelCollection<Model>> = new Set<ModelCollection<Model>>();

  createEmbeddedObject = {};

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {};
  }

  get uniqueId() {
    return this.id;
  }

  loadRootFromJson(json: object) {
    this.rootModel = this;
    this.loadFromJson(json);
    return this;
  }

  loadFromJson(json, root: Model = null, options: any = {}) {
    const embeddedModels = this.EMBEDDED_MODELS;
    let item = this;
    if (json) {
      const clientIdOrId = json.client_id || json.id;
      if (clientIdOrId && options.withRepo !== false) {
        this.runWithRepo((repo) => {
          const repoItem = repo.find(clientIdOrId);
          if (repoItem && repoItem !== item) {
            Logger.errorOnce("WARNING", "object being loaded has different reference than existing repo item. This object probably hasn't been loaded with the data you're expecting", 'this: ', this);
            item = repoItem as this;
          }
        });
      }
    }

    // The concept of a single "rootModel" does not work when
    // multiple "rootModels" end up loading the same child
    // e.g. two list views that load the same published object model as an embedded object
    if (!item.rootModel) {
      item.rootModel = item.rootModel || root || item;
    }

    for (const keyName in json) {
      if (options.except) {
        if (options.except.includes(keyName)) {
          continue;
        }
      } else if (options.only) {
        if (!options.only.includes(keyName)) {
          continue;
        }
      }

      if (Object.keys(embeddedModels).includes(keyName)) {
        item.loadEmbeddedModel(keyName, json, options);
      } else {
        item.loadProperty(keyName, json, options);
      }
    }
    if (options.withRepo !== false) {
      item.storeInRepo();
    }
    item.afterLoadFromJson();
    return item;
  }

  protected loadProperty(keyName: string, json: any, options: any = {}) {
    Vue.set(this, keyName, json[keyName]);
  }

  protected loadEmbeddedModel(keyName: string, json: any, options: any = {}): void {
    const embeddedModels = this.EMBEDDED_MODELS;
    const result = null;
    if (Array.isArray(json[keyName])) {
      //remove items that have been deleted on server
      if (this[keyName]) {
        const newIds = json[keyName].map(k => k.client_id || k.id);
        if (embeddedModels[keyName].relationType === RelationType.array) {
          const deletedItems = this[keyName].filter(item => !newIds.includes(item.id));
          deletedItems.forEach(item => {
            const index = this[keyName][this[keyName].indexOf(item)];
            Vue.delete(this[keyName], index);
          });
        } else if (embeddedModels[keyName].relationType === RelationType.collection) {
          // TODO: handle deleting items when type is collection
        } else {
          const deletedItemKeys = Object.keys(this[keyName]).filter(k => !newIds.includes(k));
          deletedItemKeys.forEach(key => {
            const singularKeyName = keyName.slice(0, -1);
            if (store.state.interfaceStore && store.state.interfaceStore[`selected_${singularKeyName}`]) {
              if (store.state.interfaceStore[`selected_${singularKeyName}`] === this[keyName][key]) {
                store.commit(`interfaceStore/deselect_${singularKeyName}`);
              }
            }
            Vue.delete(this[keyName], key);
          });
        }
      }

      //create new items from server
      if (embeddedModels[keyName].relationType === RelationType.array) {
        this[keyName] = json[keyName].map(modelJson => {
          const obj = (this[keyName] && this[keyName].find(o => (o.id && o.id === modelJson.id) || (o.client_id && o.client_id === modelJson.client_id))) ||
            ((typeof this.createEmbeddedObject[keyName] === 'function') && this.createEmbeddedObject[keyName](this, modelJson)) ||
            new embeddedModels[keyName].type();
          this.loadObject(obj, keyName, true, modelJson, options);
          return obj;
        });
      } else {
        if (embeddedModels[keyName].relationType === RelationType.dictionary && !this[keyName]) {
          Vue.set(this, keyName, {});
        }
        json[keyName].forEach(modelJson => {
          let clientIdOrId = modelJson.client_id || modelJson.id;
          const ids = []
          if (modelJson.client_id) ids.push(modelJson.client_id)
          if (modelJson.id) ids.push(modelJson.id)

          const obj = this[keyName][clientIdOrId] ||
            (embeddedModels[keyName].type.repo && embeddedModels[keyName].type.repo.findById(...ids)) ||
            ((typeof this.createEmbeddedObject[keyName] === 'function') && this.createEmbeddedObject[keyName](this, modelJson)) ||
            new embeddedModels[keyName].type();
          if (!clientIdOrId) { // guards for modelJson.client_id and modelJson.id both being null
            clientIdOrId = obj.clientIdOrId;
          }
          this.loadObject(obj, keyName, true, modelJson, options);
          if (embeddedModels[keyName].relationType === RelationType.collection) {
            this[keyName].add(obj);
          } else {
            Vue.set(this[keyName], clientIdOrId, obj);
          }
        });
      }
    } else {
      const modelJson = json[keyName];
      if (modelJson) {
        const ids = []
        if (modelJson.client_id) ids.push(modelJson.client_id)
        if (modelJson.id) ids.push(modelJson.id)

        const item = this[keyName] ||
          (embeddedModels[keyName].type.repo && embeddedModels[keyName].type.repo.findById(...ids)) ||
          ((typeof this.createEmbeddedObject[keyName] === 'function') && this.createEmbeddedObject[keyName](this, modelJson)) ||
          new embeddedModels[keyName].type();
        this.loadObject(item, keyName, false, modelJson, options);
        Vue.set(this, keyName, item);
      } else {
        Vue.set(this, keyName, null);
      }
    }
  }

  protected loadObject(obj: any, keyName: string, isPartOfCollection: boolean, json: any, options: any = {}): any {
    obj.parent = this;
    obj.rootModel = this.rootModel;
    obj.loadFromJson(json, this.rootModel, options);
    return obj;
  }

  protected storeInRepo() {
    this.runWithRepo((repo) => {
      if (this.clientIdOrId) {
        repo.add(this);
      }
    });
  }

  get clientIdOrId() {
    return this.client_id || this.id;
  }

  destroy() {
    super.destroy()
    this._collectionReferences.forEach(collection => collection.remove(this));
    this.runWithRepo((repo) => {
      repo.remove(this);
    });
  }

  addCollectionReference(collection: ModelCollection<Model>): void {
    this._collectionReferences.add(collection);
  }

  asJson(options = {}): Hash<string> {
    // TODO: Temporary - remove once tracked and untracked model's asJson are merged
    throw new Error('asJson not defined on inherited Model');
  }

  asJsonForLoad(options: any = {}) {
    options.forLoad = true;
    return this.asJson(options);
  }

  removeCollectionReference(collection: ModelCollection<Model>): void {
    this._collectionReferences.delete(collection);
  }

  // Repo & Provider Helpers
  runWithRepo(f: (repo: IRepo<this, IProvider<this>>) => any) {
    if (this.repo) {
      f(this.repo);
    }
  }

  runWithProvider(f: (provider: this['repo']['provider']) => any) {
    this.runWithRepo(repo => {
      if (this.provider) {
        f(this.provider);
      }
    });
  }

  // provider helper methods
  save(objJson?: object): Promise<IServerData<any>> {
    return new Promise<IServerData>((resolve, reject) => {
      this.runWithRepo((repo) => {
        repo.save(this, objJson).then(resolve).catch(reject);
      });
    });
  }

  refresh(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.runWithProvider((provider) => {
        provider.fetchOne(this.id).then(([response]) => {
          const data = response[0];
          if (data.success) {
            provider.createOrUpdateFrom(null, data[provider.modelRoot]);
          }
          resolve();
        }).catch(reject);
      });
    });
  }

  idForProp(prop: string): string {
    return `${StringHelper.toDash(prop)}-${this.clientIdOrId}`;
  }

  afterDestroy() {
    // override in inherited class
  }

  afterLoadFromJson() {
    // override in inherited class
  }

  get docType(): DocumentType {
    return DocumentType[this.classKey];
  }

  get ModelType(): ModelType{
    // I think this is just used for documents (forms, licenses, etc), not for others like salesforce, etc
    return DocumentTypeToModelTypeMap[this.classKey];
  }

  get modelType(): ModelType {
    Logger.logOnce('ModelType', 'model type not defined')
    return null
  }

  getRoute(actionName: commonActions ){
    return RouteMap[this.ModelType][actionName]
  }

  get classKey(): string {
    return 'model';
  }

  _frozen = false
  get frozen() {
    return this._frozen
  }

  set frozen(val) {
    this._frozen = val === true
  }

  freeze() {
    this.frozen = true
  }

  thaw() {
    this.frozen = false
  }
}
